import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import Pagination from "../Pagination/Pagination";
import BetweenAds from "../../Adds/BetweenAds";
import icon from "../../../assets/icon.svg";
import icon1 from "../../../assets/icon1.svg";
import squareMeters from "../../../assets/SquareMeters.svg";
import { FiHeart } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import { AuthModal } from "../../../models/AuthModal";
import { CollectionPickerModal } from "../../Collections/CollectionPickerModal";
import CollectionCreateModal from "../../Collections/CollectionCreateModal";
import { Home } from "lucide-react";
import { CurrencySymbol } from "../../../utils/enums";

const PropertyListEmptyState = ({ browseTo }) => {
  const ctaLabel = String(browseTo).includes("rent")
    ? "Browse rentals"
    : "Browse properties";
  return (
    <div className="w-full max-w-[910px] mx-auto bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[280px] shadow-sm">
      <div
        className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F3F6F9] flex items-center justify-center mb-5 ring-1 ring-[#EAEAEA]"
        aria-hidden
      >
        <Home className="text-[#097FE6]" size={36} strokeWidth={1.75} />
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
        No properties to show
      </h3>
      <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
        There aren&apos;t any listings in this view right now. Try adjusting filters or explore more on the main search page.
      </p>
      <Link to={browseTo} className="w-full sm:w-auto">
        <button
          type="button"
          className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
        >
          {ctaLabel}
        </button>
      </Link>
    </div>
  );
};

const PropertyListErrorState = ({ onRetry, browseTo }) => {
  const ctaLabel = String(browseTo).includes("rent")
    ? "Browse rentals"
    : "Browse properties";
  return (
    <div className="w-full max-w-[910px] mx-auto bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[280px] shadow-sm">
      <div
        className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#FEF2F2] flex items-center justify-center mb-5 ring-1 ring-[#FECACA]"
        aria-hidden
      >
        <span className="text-2xl sm:text-3xl font-semibold text-[#DC2626]" aria-hidden>
          !
        </span>
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
        Couldn&apos;t load listings
      </h3>
      <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
        Something went wrong while fetching properties. Check your connection and try again.
      </p>
      {typeof onRetry === "function" ? (
        <button
          type="button"
          onClick={onRetry}
          className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
        >
          Try again
        </button>
      ) : (
        <Link to={browseTo} className="w-full sm:w-auto">
          <button
            type="button"
            className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
          >
            {ctaLabel}
          </button>
        </Link>
      )}
    </div>
  );
};

const PropertyCard = ({
  property,
  isAuthenticate,
  setAuthPopUp,
  setPickerOpen,
  setPropertyId,
  onCreateNewCollection,
}) => {
  const navigate = useNavigate();
  const p = property || {};
  const images = p?.frontPageImg || [];
  const mainImage = images[0];
  const handleCardClick = () => {
    navigate(`/property-house/${p?._id || p?.id}`);
  };

  const handleFavorite = () => {
    if (!isAuthenticate && setAuthPopUp) {
      setAuthPopUp(true);
      return;
    }
    setPropertyId(p?._id || p?.id);
    setPickerOpen && setPickerOpen(true);
  };

  const priceText =
    p?.price && p?.price_display_checked === "show_actual_price"
      ? `${CurrencySymbol.NGN} ` + String(p.price).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      : p?.price_display || null;

  const propertyTitle =
    p?.listingTitle ||
    p?.title ||
    p?.street_address_name ||
    `${p?.street_address_number || ""} ${p?.street_address_name || ""}`.trim() ||
    "Property";

  const fullAddress = [
    p?.street_address_number,
    p?.street_address_name,
    p?.suburb,
    p?.municipality,
  ]
    .filter(Boolean)
    .join(", ");

  const areaText = p?.type?.startsWith("House") ? p?.houseSize : p?.land_size;

  const agencyCircleImg =
    p?.lead_agent?.profileImg ||
    p?.agentImg ||
    p?.lead_agent?.agencyLogo ||
    p?.agencyImg;
  const agentName = p?.lead_agent?.name || "";

  return (
    <div
      className="flex flex-col bg-white rounded-[24px] w-full overflow-hidden border-[1.5px] border-[#EAEAEA] hover:shadow-md transition-all duration-200 cursor-pointer"
      onClick={handleCardClick}
    >
      <div
        className="relative w-full h-[220px] sm:h-[300px] md:h-[380px] lg:h-[520px] xl:h-[571px] bg-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        {images.length > 1 ? (
          <Swiper
            modules={[Navigation]}
            navigation
            className="property-card-swiper w-full h-full"
            style={{ margin: 0 }}
          >
            {images.map((src, idx) => (
              <SwiperSlide
                key={idx}
                className="!w-full !h-full cursor-pointer"
                onClick={handleCardClick}
              >
                <img
                  src={src}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : mainImage ? (
          <img
            src={mainImage}
            alt=""
            className="w-full h-full object-cover cursor-pointer"
            loading="lazy"
            onClick={handleCardClick}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#737373] text-sm">
            No image
          </div>
        )}
        <div
          className={`absolute inset-x-0 top-0 h-12 z-[50] lg:h-14 flex justify-between items-center px-4 ${p?.primary_color ? "" : "bg-black"}`}
          style={p?.primary_color ? { backgroundColor: p.primary_color } : undefined}
        >
          <div className="flex items-center min-w-0">
            {p?.lead_agent?.agencyLogo || p?.agencyImg ? (
              <LazyLoadImage
                src={p?.lead_agent?.agencyLogo || p?.agencyImg}
                alt=""
                className="h-10 md:h-14 w-auto py-2 object-contain"
                effect="blur"
                onClick={() => navigate(`/agency-profile/${p?.lead_agent?.agency_id}`)}
              />
            ) : (
              <span className="text-white font-medium text-sm truncate" onClick={() => navigate(`/agency-profile/${p?.lead_agent?.agency_id}`)}>
                {p?.lead_agent?.name || "Agency"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-white lg:text-[22px] text-sm md:text-base capitalize font-medium truncate max-w-[180px] mr-12 md:mr-20 lg:mr-28">
              {agentName}
            </span>
            <div className="absolute bottom-0 right-3 z-10 flex-shrink-0 top-5 flex items-center justify-center h-12 w-12 md:h-20 md:w-20 rounded-full border-2 border-white shadow bg-white/10 overflow-hidden">
              {agencyCircleImg ? (
                <LazyLoadImage
                  src={agencyCircleImg}
                  alt=""
                  className="object-cover h-12 w-12 md:h-20 md:w-20"
                  effect="blur"
                  onClick={() => navigate(`/agent-profile/${p?.lead_agent?.id}`)}
                />
              ) : (
                <span className="text-white font-semibold text-lg md:text-xl" onClick={() => navigate(`/agent-profile/${p?.lead_agent?.id}`)}>
                  {agentName ? agentName.charAt(0).toUpperCase() : "A"}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 md:p-6">
        <div className="flex justify-between items-start md:gap-4 gap-2">
          <div className="min-w-0 flex-1 flex justify-between items-start gap-1 lg:gap-4 flex-col">
            {priceText && (
              <p className="text-[#097FE6] font-bold md:text-[24px] text-[16px] md:text-xl">
                {priceText}
              </p>
            )}
            <p className="text-[#171717] font-bold text-base md:text-[30px] mt-0.5 ">
              {propertyTitle}
            </p>
            {fullAddress && (
              <p className="text-[#000929] opacity-50 font-medium text-base text-[14px] md:text-[18px] mt-0.5 line-clamp-1">
                {fullAddress}
              </p>
            )}
          </div>
          <button
            type="button"
            className="shrink-0 p-2 md:p-3 mt-0.5 bg-white rounded-full border border-[#E5E5E5] hover:border-[#097FE6] transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleFavorite();
            }}
            aria-label="Save property"
          >
            {isAuthenticate && p?.collections?.length > 0 ? (
              <FaHeart color="#097FE6" size={20} />
            ) : (
              <FiHeart color="#097FE6" size={20} />
            )}
          </button>
        </div>

        <div className=" border-t-0 lg:border-t-[1.8px] border-[#EAEAEA] mt-2 lg:mt-4 pt-2 lg:pt-4">
          <div className="flex flex-wrap items-center gap-3 lg:gap-6 text-[#171717] text-sm md:text-base font-medium">
            {p?.bedroomCount != null && (
              <span className="flex items-center gap-2">
                <img src={icon1} alt="icon" className="w-5 h-5 shrink-0" />
                {p.bedroomCount} Beds
              </span>
            )}
            {p?.showerCount != null && (
              <span className="flex items-center gap-2">
                <img src={icon} alt="icon" className="w-5 h-5 shrink-0" />
                {p.showerCount} Bathrooms
              </span>
            )}
            {areaText && (
              <span className="flex items-center gap-2">
                <img
                  src={squareMeters}
                  alt="icon"
                  className="w-5 h-5 shrink-0"
                />
                {areaText} m²
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PAGE_SIZE = 5;

export const PropertyCardList = ({
  PropertyData = [],
  setPropertyData,
  style = "",
  adsList = [],
  fetchError = false,
  onRetry,
  browseTo = "/buy",
}) => {
  const list = Array.isArray(PropertyData) ? PropertyData : [];
  const [currentPage, setCurrentPage] = useState(1);
  const [AuthPopUp, setAuthPopUp] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [propertyId, setPropertyId] = useState(null);
  const [SavedProperty, setSavedProperty] = useState(
    () => localStorage.getItem("Saved")?.split(",").filter(Boolean) || [],
  );
  const isAuthenticate = localStorage.getItem("Token");
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const currentData = list.slice(startIndex, endIndex);

  React.useEffect(() => {
    if (!setPropertyData) return;
    setPropertyData((prev) =>
      prev?.map((x) => ({
        ...x,
        favourite: SavedProperty?.includes(x?.id),
      })),
    );
  }, [SavedProperty, setPropertyData]);

  return (
    <>
      <div className={`flex flex-col gap-8 ${style}`}>
        {fetchError ? (
          <PropertyListErrorState onRetry={onRetry} browseTo={browseTo} />
        ) : list.length > 0 ? (
          currentData.map((item, index) => (
            <React.Fragment key={item?.id || item?._id || index}>
              <PropertyCard
                property={item}
                isAuthenticate={isAuthenticate}
                setAuthPopUp={setAuthPopUp}
                setPickerOpen={setPickerOpen}
                propertyId={propertyId}
                setPropertyId={setPropertyId}
                initialSelectedIds={item?.collections?.map((col) => col.id)}
              />
              {index % 2 !== 0 && adsList?.[Math.floor(index / 2)] && (
                <BetweenAds detail={adsList[Math.floor(index / 2)]} />
              )}
            </React.Fragment>
          ))
        ) : (
          <PropertyListEmptyState browseTo={browseTo} />
        )}
      </div>

      {!fetchError && list.length > 0 && (
        <div className="w-full flex justify-start">
          <Pagination
            className="pagination-bar mb-2"
            currentPage={currentPage}
            totalCount={list.length}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
          />
        </div>
      )}
      {pickerOpen && (
        <CollectionPickerModal
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          propertyId={propertyId}
          initialSelectedIds={
            list.find((x) => x.id === propertyId)?.collections?.map(
              (col) => col.id ?? col._id ?? col.collectionId,
            ) || []
          }
          onCreateNewCollection={() => setCreateModalOpen(true)}
        />
      )}
      {createModalOpen && (
        <CollectionCreateModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          editingCollection={null}
          onSaved={() => { }}
        />
      )}

      {AuthPopUp && (
        <AuthModal open={AuthPopUp} onClose={() => setAuthPopUp(false)} />
      )}
    </>
  );
};

export default PropertyCard;
